import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Warehouse, Package, Boxes, ChevronUp, ChevronDown, ChevronsUpDown, X, Check, ImageIcon, SlidersHorizontal, Copy, Download, GripVertical, ArrowLeftRight, Eye, EyeOff, Printer, Loader2, ClipboardCheck, Upload, AlertCircle, History, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadText, downloadFromUrl } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { PageHeader } from "@/components/PageHeader";
import { ColumnsToolbar } from "@/components/ColumnsToolbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuCheckboxItem, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const priceCell = (n) => (Number(n) > 0 ? money(n) : "—");
const EMPTY = {
  name: "", sku: "", description: "", unit_price: 0, recurring_price: 0, currency: "CAD",
  category: "", status: "active", stock: {}, manufacturer: "", supplier: "", supplier_sku: "",
  weight: 0, recurring_period: "No", cad_msrp: 0, cad_wholesale: 0, usd_msrp: 0, usd_wholesale: 0, images: [], vendors: [],
};
const NUMERIC_SORT = new Set(["cad_msrp", "cad_wholesale", "usd_msrp", "usd_wholesale", "total_stock"]);
const TOGGLE_COLS = [
  { key: "sku", label: "SKU" },
  { key: "manufacturer", label: "Manufacturer" },
  { key: "supplier", label: "Supplier" },
  { key: "status", label: "Status" },
  { key: "usd_msrp", label: "USD MSRP" },
  { key: "usd_wholesale", label: "USD WS" },
];
const COST_COLS = [
  { key: "vendor", label: "Primary Vendor" },
  { key: "cost_cad", label: "UC Cost (CAD)" },
  { key: "cost_usd", label: "UC Cost (USD)" },
  { key: "margin", label: "Margin %" },
];
const DEFAULT_COLS = { sku: false, manufacturer: false, supplier: true, usd_msrp: false, usd_wholesale: false, vendor: true, cost_cad: true, cost_usd: false, margin: true };
const primaryVendor = (it) => (it.vendors || []).find((v) => v.rank === "primary") || (it.vendors || [])[0] || null;
const marginPct = (it) => {
  const pv = primaryVendor(it);
  const msrp = Number(it.cad_msrp) || 0;
  const cost = pv ? Number(pv.cost_cad) || 0 : 0;
  if (!msrp || !cost) return null;
  return ((msrp - cost) / msrp) * 100;
};
const RANK_ORDER = { primary: 0, secondary: 1, other: 2 };
const orderedVendors = (it) => [...(it.vendors || [])].sort((a, b) => (RANK_ORDER[a.rank] ?? 3) - (RANK_ORDER[b.rank] ?? 3));
const RANK_BADGE = { primary: "bg-amber-100 text-amber-700", secondary: "bg-slate-100 text-slate-600", other: "bg-slate-50 text-slate-400" };
const VENDOR_RANKS = [{ v: "primary", l: "Primary" }, { v: "secondary", l: "Secondary" }, { v: "other", l: "Other" }];
const LS_KEY = "inv_prefs_v1";
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; } };
const csvCell = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

export default function Inventory() {
  const { has } = useAuth();
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  // Focus filter: "all" shows every warehouse column, otherwise only the picked
  // warehouse's column is shown AND the item list is filtered to rows with
  // stock in that warehouse (or all rows when `whFocusIncludeZero` is on).
  // Default target = "HQ" if it exists, else "all". Persisted per-user.
  const [whFocus, setWhFocus] = useState(() => {
    try { return localStorage.getItem("inv.whFocus") || "auto"; } catch { return "auto"; }
  });
  const [whFocusIncludeZero, setWhFocusIncludeZero] = useState(() => {
    try { return localStorage.getItem("inv.whFocusIncludeZero") === "1"; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem("inv.whFocus", whFocus); } catch { /* silent */ } }, [whFocus]);
  useEffect(() => { try { localStorage.setItem("inv.whFocusIncludeZero", whFocusIncludeZero ? "1" : "0"); } catch { /* silent */ } }, [whFocusIncludeZero]);
  // First load: if the user hasn't picked yet and the warehouses list finally
  // arrives, snap the default to "HQ" (case-insensitive match) so day-one
  // usage matches "the most used warehouse".
  useEffect(() => {
    if (whFocus !== "auto" || !warehouses.length) return;
    const hq = warehouses.find((w) => (w.name || "").trim().toLowerCase() === "hq");
    setWhFocus(hq ? hq.warehouse_id : "all");
  }, [warehouses, whFocus]);
  const [costAccess, setCostAccess] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);
  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState({ name: "", location: "" });
  const [whEdit, setWhEdit] = useState(null);

  // Count-sheet dialog state. Defaults: warehouse = first available, search =
  // whatever the user has typed into the inventory search bar (so the button
  // acts as "print what I'm looking at"), date = today.
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const [csOpen, setCsOpen] = useState(false);
  const [csWid, setCsWid] = useState("");
  const [csSearch, setCsSearch] = useState("");
  const [csDate, setCsDate] = useState(todayISO());
  const [csIncludeZero, setCsIncludeZero] = useState(true);
  const [csLoading, setCsLoading] = useState(false);
  const openCountSheet = () => {
    setCsWid((prev) => prev || warehouses[0]?.warehouse_id || "");
    setCsSearch(search || "");
    setCsDate(todayISO());
    setCsOpen(true);
  };
  const downloadCountSheet = async () => {
    if (!csWid) { toast.error("Pick a warehouse first"); return; }
    setCsLoading(true);
    try {
      const params = { warehouse_id: csWid, count_date: csDate, include_zero: csIncludeZero };
      if (csSearch.trim()) params.search = csSearch.trim();
      const { data } = await api.get("/items/count-sheet.pdf", { params });
      if (!data?.token) throw new Error("no token");
      await downloadFromUrl(`/files/${data.token}`);
      toast.success(`Count sheet ready — ${data.count} item(s)`);
      setCsOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed");
    } finally { setCsLoading(false); }
  };

  // ── Reconcile counts ────────────────────────────────────────────────
  // Grid state: { [item_id]: "counted string" }. Left blank until the user
  // types anything, then the row participates in the reconciliation.
  const [rcOpen, setRcOpen] = useState(false);
  const [rcWid, setRcWid] = useState("");
  const [rcSearch, setRcSearch] = useState("");
  const [rcNotes, setRcNotes] = useState("");
  const [rcCounts, setRcCounts] = useState({});
  const [rcPreview, setRcPreview] = useState(null);   // dry-run response
  const [rcSubmitting, setRcSubmitting] = useState(false);
  const openReconcile = () => {
    setRcWid((prev) => prev || warehouses[0]?.warehouse_id || "");
    setRcSearch(search || "");
    setRcNotes("");
    setRcCounts({});
    setRcPreview(null);
    setRcOpen(true);
  };
  // Compute the reconcile grid: same set the user sees, filtered to the picked
  // warehouse. We surface `expected` = current stock in that warehouse.
  const reconcileRows = (() => {
    if (!rcWid) return [];
    const needle = rcSearch.trim().toLowerCase();
    return items
      .filter((it) => (it.status || "active").toLowerCase() === "active")
      .filter((it) => {
        if (!needle) return true;
        return `${it.name} ${it.sku} ${it.manufacturer} ${it.supplier} ${it.category}`.toLowerCase().includes(needle);
      })
      .map((it) => ({
        item_id: it.item_id,
        name: it.name,
        sku: it.sku,
        expected: Number((it.stock || {})[rcWid] || 0),
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  })();
  const rcTouchedIds = Object.keys(rcCounts).filter((id) => rcCounts[id] !== "" && rcCounts[id] != null);
  const rcPayload = () => ({
    warehouse_id: rcWid,
    notes: rcNotes.trim(),
    counts: rcTouchedIds.map((id) => ({ item_id: id, counted: Math.max(0, parseInt(rcCounts[id] || "0", 10) || 0) })),
  });
  const runReconcilePreview = async () => {
    if (!rcTouchedIds.length) { toast.error("Type at least one count first"); return; }
    setRcSubmitting(true);
    try {
      const { data } = await api.post("/items/reconcile-counts", { ...rcPayload(), dry_run: true });
      setRcPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setRcSubmitting(false); }
  };
  const runReconcileApply = async () => {
    if (!rcPreview) { await runReconcilePreview(); return; }
    setRcSubmitting(true);
    try {
      const { data } = await api.post("/items/reconcile-counts", { ...rcPayload(), dry_run: false });
      toast.success(`Reconciled ${data.adjusted} item(s) at ${data.warehouse_name} · ${data.unchanged} unchanged`);
      setRcOpen(false);
      load();  // refresh items list
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reconciliation failed");
    } finally { setRcSubmitting(false); }
  };
  // CSV upload: two-column CSV `sku,counted` (or `item_id,counted`) — anything
  // else is ignored. Prefills the grid so the user can still review.
  const rcUploadCsv = async (evt) => {
    const file = evt.target.files?.[0];
    evt.target.value = "";
    if (!file) return;
    const text = await file.text();
    const bySku = new Map(items.map((it) => [(it.sku || "").toLowerCase(), it.item_id]));
    const patch = {};
    let matched = 0, missed = 0;
    text.split(/\r?\n/).forEach((line) => {
      const cols = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
      if (cols.length < 2) return;
      const [k, v] = [cols[0], cols[1]];
      if (!k || !/^\d+$/.test(v)) return;
      // Match by item_id first, else by SKU (case-insensitive).
      const iid = bySku.has(k.toLowerCase()) ? bySku.get(k.toLowerCase())
                : items.find((it) => it.item_id === k)?.item_id;
      if (iid) { patch[iid] = v; matched++; } else { missed++; }
    });
    if (!matched) { toast.error("No rows matched by SKU or item_id"); return; }
    setRcCounts((prev) => ({ ...prev, ...patch }));
    setRcPreview(null);
    toast.success(`Loaded ${matched} count(s) from CSV${missed ? ` · ${missed} unmatched skipped` : ""}`);
  };

  // ── Reconciliation history ─────────────────────────────────────────
  // Two modes: `list` shows recent reconciliations for the picked warehouse,
  // `detail` drills into one reconciliation's full movement list.
  const [histOpen, setHistOpen] = useState(false);
  const [histWid, setHistWid] = useState("all");
  const [histLoading, setHistLoading] = useState(false);
  const [histRows, setHistRows] = useState([]);
  const [histDetail, setHistDetail] = useState(null);      // reconciliation detail object
  const [histDetailLoading, setHistDetailLoading] = useState(false);
  const loadHistory = async (wid) => {
    setHistLoading(true);
    setHistDetail(null);
    try {
      const params = {};
      if (wid && wid !== "all") params.warehouse_id = wid;
      const { data } = await api.get("/inventory/reconciliations", { params });
      setHistRows(data.reconciliations || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load history");
    } finally { setHistLoading(false); }
  };
  const openHistory = () => {
    const initialWid = (isFocused ? whFocus : "all");
    setHistWid(initialWid);
    setHistOpen(true);
    loadHistory(initialWid);
  };
  const openHistoryDetail = async (rid) => {
    setHistDetailLoading(true);
    try {
      const { data } = await api.get(`/inventory/reconciliations/${rid}`);
      setHistDetail(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load reconciliation");
    } finally { setHistDetailLoading(false); }
  };

  const canCreate = has("inventory.create");
  const canEdit = has("inventory.edit");
  const canManage = has("inventory.edit");

  const load = () => {
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/items/cost-access").then((r) => setCostAccess(!!r.data.can_view)).catch(() => setCostAccess(false)); }, []);

  const addVendor = () => setForm((f) => ({ ...f, vendors: [...(f.vendors || []), { vendor: "", rank: (f.vendors || []).some((v) => v.rank === "primary") ? "secondary" : "primary", cost_cad: 0, cost_usd: 0 }] }));
  const updVendor = (i, k, v) => setForm((f) => ({ ...f, vendors: (f.vendors || []).map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const rmVendor = (i) => setForm((f) => ({ ...f, vendors: (f.vendors || []).filter((_, idx) => idx !== i) }));

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, images: [] }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it, stock: it.stock || {}, images: it.images || [] }); setOpen(true); };
  const cloneItem = (it) => {
    setEditing(null);
    setForm({ ...EMPTY, ...it, name: `${it.name} (Copy)`, sku: it.sku ? `${it.sku}-COPY` : "", stock: {}, images: it.images || [] });
    setOpen(true);
  };

  const onImageFile = (e) => {
    const files = Array.from(e.target.files || []);
    const room = 3 - (form.images?.length || 0);
    if (room <= 0) { toast.error("Up to 3 images allowed"); return; }
    files.slice(0, room).forEach((file) => {
      if (file.size > 2 * 1024 * 1024) { toast.error(`${file.name} is over 2MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, images: [...(f.images || []), reader.result].slice(0, 3) }));
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };
  const removeImage = (idx) => setForm((f) => ({ ...f, images: (f.images || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Item name required"); return; }
    const payload = {
      ...form,
      cad_msrp: Number(form.cad_msrp), cad_wholesale: Number(form.cad_wholesale),
      usd_msrp: Number(form.usd_msrp), usd_wholesale: Number(form.usd_wholesale),
      weight: Number(form.weight), recurring_price: Number(form.recurring_price),
      unit_price: Number(form.cad_msrp) || Number(form.unit_price) || 0,
      stock: Object.fromEntries(Object.entries(form.stock).map(([k, v]) => [k, Number(v) || 0])),
      vendors: (form.vendors || []).filter((v) => (v.vendor || "").trim()).map((v) => ({
        vendor: v.vendor.trim(), rank: v.rank || "other", cost_cad: Number(v.cost_cad) || 0, cost_usd: Number(v.cost_usd) || 0,
      })),
    };
    try {
      if (editing) { await api.put(`/items/${editing.item_id}`, payload); toast.success("Item updated"); }
      else { await api.post("/items", payload); toast.success("Item created"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/items/${delTarget.item_id}`); toast.success("Item deleted"); setDelTarget(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addWarehouse = async () => {
    if (!whForm.name.trim()) { toast.error("Warehouse name required"); return; }
    try { await api.post("/warehouses", whForm); toast.success("Warehouse added"); setWhForm({ name: "", location: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delWarehouse = async (wid) => {
    try { await api.delete(`/warehouses/${wid}`); toast.success("Warehouse removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const startWhEdit = (w) => setWhEdit({ warehouse_id: w.warehouse_id, name: w.name, location: w.location || "" });
  const saveWhEdit = async () => {
    if (!whEdit.name.trim()) { toast.error("Warehouse name required"); return; }
    try { await api.put(`/warehouses/${whEdit.warehouse_id}`, { name: whEdit.name.trim(), location: whEdit.location }); toast.success("Warehouse updated"); setWhEdit(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [search, setSearch] = usePersistedState("inventory.search", "");
  // 3-state filter: "hide" (default — active only), "show" (mix of both),
  // or "only" (inactive only, for the review workflow). Migrates from the
  // legacy boolean localStorage key transparently.
  const [inactiveFilter, setInactiveFilter] = useState(() => {
    try {
      const cur = localStorage.getItem("inv.inactiveFilter");
      if (cur === "hide" || cur === "show" || cur === "only") return cur;
      // legacy migration
      return localStorage.getItem("inv.showInactive") === "1" ? "show" : "hide";
    } catch { return "hide"; }
  });
  useEffect(() => { try { localStorage.setItem("inv.inactiveFilter", inactiveFilter); } catch { /* silent */ } }, [inactiveFilter]);
  const [stockEdit, setStockEdit] = useState(null);
  const [stockVal, setStockVal] = useState("");
  const startStockEdit = (it, wid) => { setStockEdit({ itemId: it.item_id, wid }); setStockVal(String((it.stock && it.stock[wid]) || 0)); };
  const commitStock = async (it, wid) => {
    const q = Math.max(0, parseInt(stockVal || "0", 10) || 0);
    setStockEdit(null);
    try {
      const { data } = await api.post(`/items/${it.item_id}/adjust-stock`, { warehouse_id: wid, quantity: q });
      setItems((arr) => arr.map((x) => (x.item_id === it.item_id ? data : x)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [xfer, setXfer] = useState(null); // { item, from, to, qty }
  const [xferMoves, setXferMoves] = useState([]);
  const loadMoves = (iid) => api.get(`/items/${iid}/movements`).then((r) => setXferMoves(r.data || [])).catch(() => setXferMoves([]));
  const openTransfer = (it) => { setXfer({ item: it, from: "", to: "", qty: "" }); setXferMoves([]); loadMoves(it.item_id); };
  const submitTransfer = async () => {
    const q = parseInt(xfer.qty || "0", 10) || 0;
    if (!xfer.from || !xfer.to) { toast.error("Select both warehouses"); return; }
    if (xfer.from === xfer.to) { toast.error("Warehouses must differ"); return; }
    if (q <= 0) { toast.error("Enter a quantity greater than zero"); return; }
    try {
      const { data } = await api.post(`/items/${xfer.item.item_id}/transfer-stock`, { from_warehouse_id: xfer.from, to_warehouse_id: xfer.to, quantity: q });
      setItems((arr) => arr.map((x) => (x.item_id === xfer.item.item_id ? data : x)));
      toast.success("Stock transferred");
      setXfer((cur) => (cur ? { ...cur, item: data, from: "", to: "", qty: "" } : cur));
      loadMoves(xfer.item.item_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const _prefs = loadPrefs();
  const [cols, setCols] = useState({ ...DEFAULT_COLS, ...(_prefs.cols || {}) });
  const [sortKey, setSortKey] = useState(_prefs.sortKey || "name");
  const [sortDir, setSortDir] = useState(_prefs.sortDir || "asc");
  const [colOrder, setColOrder] = useState(_prefs.colOrder || []);
  const [dragKey, setDragKey] = useState(null);
  useEffect(() => { localStorage.setItem(LS_KEY, JSON.stringify({ cols, sortKey, sortDir, colOrder })); }, [cols, sortKey, sortDir, colOrder]);
  const toggleCol = (k) => setCols((c) => ({ ...c, [k]: !c[k] }));
  const toggleColDefaultOn = (k) => setCols((c) => ({ ...c, [k]: c[k] === false ? true : false }));
  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };
  const totalUnits = items.reduce((s, it) => s + (it.total_stock || 0), 0);
  const inactiveCount = items.filter((it) => (it.status || "active").toLowerCase() !== "active").length;
  const statusFiltered = (() => {
    if (inactiveFilter === "show") return items;
    if (inactiveFilter === "only") return items.filter((it) => (it.status || "active").toLowerCase() === "inactive");
    return items.filter((it) => (it.status || "active").toLowerCase() === "active");
  })();
  // Warehouse-focus filter: when a specific warehouse is picked, hide items
  // with 0 stock there unless the "include zero-stock" checkbox is on.
  const isFocused = whFocus !== "all" && whFocus !== "auto";
  const focusFiltered = (isFocused && !whFocusIncludeZero)
    ? statusFiltered.filter((it) => Number((it.stock || {})[whFocus] || 0) > 0)
    : statusFiltered;
  const filtered = search.trim()
    ? focusFiltered.filter((it) => `${it.name} ${it.sku} ${it.manufacturer} ${it.supplier} ${it.category}`.toLowerCase().includes(search.trim().toLowerCase()))
    : focusFiltered;
  const sorted = [...filtered].sort((a, b) => {
    if (NUMERIC_SORT.has(sortKey)) {
      const d = (Number(a[sortKey]) || 0) - (Number(b[sortKey]) || 0);
      return sortDir === "asc" ? d : -d;
    }
    const av = (a[sortKey] || "").toString().toLowerCase();
    const bv = (b[sortKey] || "").toString().toLowerCase();
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const ipg = useTablePagination(sorted, 50);

  const exportCsv = () => {
    const headers = ["Item Name", "Category", "SKU", "Manufacturer", "Supplier", "Supplier SKU", "CAD MSRP", "CAD Wholesale", "USD MSRP", "USD Wholesale", "Recurring", "Weight", "Total Stock"];
    if (costAccess) headers.push("Primary Vendor", "UC Cost CAD", "UC Cost USD", "Margin %", "All Vendor Costs");
    const rows = sorted.map((it) => {
      const base = [it.name, it.category, it.sku, it.manufacturer, it.supplier, it.supplier_sku, it.cad_msrp, it.cad_wholesale, it.usd_msrp, it.usd_wholesale, it.recurring_period, it.weight, it.total_stock || 0];
      if (costAccess) {
        const pv = primaryVendor(it);
        const m = marginPct(it);
        const all = (it.vendors || []).map((v) => `${v.vendor} (${v.rank}): CAD ${v.cost_cad}/USD ${v.cost_usd}`).join(" | ");
        base.push(pv?.vendor || "", pv?.cost_cad ?? "", pv?.cost_usd ?? "", m === null ? "" : `${m.toFixed(1)}%`, all);
      }
      return base;
    });
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
    downloadText(csv, `inventory-export-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} items`);
  };

  const baseOrder = ["name", "sku", "manufacturer", "supplier", "cad_msrp", "cad_wholesale", "usd_msrp", "usd_wholesale", "recurring_period", "vendor", "cost_cad", "cost_usd", "margin", ...warehouses.map((w) => `wh:${w.warehouse_id}`), "total_stock"];
  const mergedOrder = colOrder.filter((k) => baseOrder.includes(k)).concat(baseOrder.filter((k) => !colOrder.includes(k)));

  const isVisible = (key) => {
    if (["sku", "manufacturer", "supplier", "usd_msrp", "usd_wholesale"].includes(key)) return cols[key];
    if (["vendor", "cost_cad", "cost_usd", "margin"].includes(key)) return costAccess && cols[key];
    if (key.startsWith("wh:")) {
      // When focused on one warehouse, hide the others; when "all", honour the per-column toggle.
      if (isFocused && key !== `wh:${whFocus}`) return false;
      return cols[key] !== false;
    }
    return true;
  };

  const colDef = (key) => {
    if (key.startsWith("wh:")) {
      const wid = key.slice(3);
      const wh = warehouses.find((w) => w.warehouse_id === wid);
      return { key, label: wh?.name || "WH", wpx: 88, w: "w-[88px]", align: "right", sort: null, tdClass: "text-slate-600",
        render: (it) => {
          const ed = stockEdit && stockEdit.itemId === it.item_id && stockEdit.wid === wid;
          const val = (it.stock && it.stock[wid]) || 0;
          return canEdit ? (ed ? (
            <Input autoFocus type="number" value={stockVal} data-testid={`stock-input-${it.item_id}-${wid}`}
              onChange={(e) => setStockVal(e.target.value)} onBlur={() => commitStock(it, wid)}
              onKeyDown={(e) => { if (e.key === "Enter") commitStock(it, wid); if (e.key === "Escape") setStockEdit(null); }}
              className="ml-auto h-7 w-16 text-right" />
          ) : (
            <button data-testid={`stock-cell-${it.item_id}-${wid}`} onClick={() => startStockEdit(it, wid)}
              className="min-w-[2rem] rounded px-1.5 py-0.5 hover:bg-primary/10 hover:text-primary">{val}</button>
          )) : val;
        } };
    }
    const R = {
      name: { label: "Item", wpx: 240, w: "w-[240px]", align: "left", sort: "name", tdClass: "whitespace-normal break-words",
        render: (it) => {
          const isInactive = (it.status || "active").toLowerCase() === "inactive";
          return (
            <>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-slate-800">{it.name}</span>
                {isInactive && (
                  <span data-testid={`item-inactive-pill-${it.item_id}`}
                    title="This item is marked inactive — it won't appear on new quotes."
                    className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                    Inactive
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400">{it.category}</div>
            </>
          );
        } },
      sku: { label: "SKU", wpx: 112, w: "w-28", align: "left", sort: "sku", tdClass: "whitespace-normal break-words font-mono text-xs text-slate-500", render: (it) => it.sku || "—" },
      manufacturer: { label: "Manufacturer", wpx: 128, w: "w-32", align: "left", sort: "manufacturer", tdClass: "whitespace-normal break-words text-slate-600", render: (it) => it.manufacturer || "—" },
      supplier: { label: "Supplier", wpx: 128, w: "w-32", align: "left", sort: "supplier", tdClass: "whitespace-normal break-words text-slate-600", render: (it) => it.supplier || "—" },
      cad_msrp: { label: "CAD MSRP", wpx: 96, w: "w-24", align: "right", sort: "cad_msrp", render: (it) => priceCell(it.cad_msrp) },
      cad_wholesale: { label: "CAD WS", wpx: 96, w: "w-24", align: "right", sort: "cad_wholesale", tdClass: "text-slate-500", render: (it) => priceCell(it.cad_wholesale) },
      usd_msrp: { label: "USD MSRP", wpx: 96, w: "w-24", align: "right", sort: "usd_msrp", tdClass: "text-slate-500", render: (it) => priceCell(it.usd_msrp) },
      usd_wholesale: { label: "USD WS", wpx: 96, w: "w-24", align: "right", sort: "usd_wholesale", tdClass: "text-slate-500", render: (it) => priceCell(it.usd_wholesale) },
      recurring_period: { label: "Recurring", wpx: 96, w: "w-24", align: "left", sort: "recurring_period",
        render: (it) => it.recurring_period && it.recurring_period !== "No" ? <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600">{it.recurring_period}</span> : <span className="text-slate-400">—</span> },
      vendor: { label: "Vendors", wpx: 132, w: "w-[132px]", align: "left", sort: null, headClass: "text-amber-600", tdClass: "whitespace-normal break-words text-slate-700", testid: (it) => `vendor-cell-${it.item_id}`,
        render: (it) => orderedVendors(it).length ? <div className="space-y-1">{orderedVendors(it).map((v, vi) => <div key={vi} className="flex h-6 items-center gap-1.5"><span className="font-medium">{v.vendor}</span><span className={`rounded px-1 py-0.5 text-[10px] font-medium capitalize ${RANK_BADGE[v.rank] || RANK_BADGE.other}`}>{v.rank}</span></div>)}</div> : <span className="text-slate-400">—</span> },
      cost_cad: { label: "UC Cost CAD", wpx: 104, w: "w-[104px]", align: "right", sort: null, headClass: "text-amber-600", tdClass: "font-medium text-amber-700", testid: (it) => `cost-cad-${it.item_id}`,
        render: (it) => orderedVendors(it).length ? <div className="space-y-1">{orderedVendors(it).map((v, vi) => <div key={vi} className="flex h-6 items-center justify-end">{v.cost_cad ? money(v.cost_cad) : "—"}</div>)}</div> : "—" },
      cost_usd: { label: "UC Cost USD", wpx: 104, w: "w-[104px]", align: "right", sort: null, headClass: "text-amber-600", tdClass: "font-medium text-amber-700",
        render: (it) => orderedVendors(it).length ? <div className="space-y-1">{orderedVendors(it).map((v, vi) => <div key={vi} className="flex h-6 items-center justify-end">{v.cost_usd ? money(v.cost_usd) : "—"}</div>)}</div> : "—" },
      margin: { label: "Margin %", wpx: 92, w: "w-[92px]", align: "right", sort: null, headClass: "text-amber-600", tdClass: "font-semibold", testid: (it) => `margin-${it.item_id}`,
        render: (it) => { const m = marginPct(it); return m === null ? <span className="text-slate-300">—</span> : <span className={m >= 0 ? "text-green-600" : "text-red-600"}>{m.toFixed(1)}%</span>; } },
      total_stock: { label: "Total", wpx: 64, w: "w-16", align: "right", sort: "total_stock", tdClass: "font-semibold text-slate-800", render: (it) => it.total_stock || 0 },
      status: { label: "Status", wpx: 92, w: "w-[92px]", align: "left", sort: "status", tdClass: "",
        render: (it) => {
          const s = (it.status || "active").toLowerCase();
          const cls = s === "active"
            ? "bg-green-50 text-green-700 border-green-200"
            : s === "inactive"
              ? "bg-slate-100 text-slate-500 border-slate-200"
              : "bg-amber-50 text-amber-700 border-amber-200";
          return <span data-testid={`item-status-${it.item_id}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${cls}`}>{s}</span>;
        } },
    };
    return { key, ...R[key] };
  };

  const visibleCols = mergedOrder.filter((k) => isVisible(k)).map(colDef);
  const tableMinW = Math.max(720, 48 + 72 + visibleCols.reduce((s, c) => s + (c.wpx || 96), 0));

  // Shared ColumnsToolbar adapters — bridges Inventory's cols/colOrder state
  // to the {id, label, always} contract used by Hardware/Partners toolbars.
  const toolbarColumns = (() => {
    const ALWAYS = [
      { id: "name",             label: "Item",      always: true },
      { id: "cad_msrp",         label: "CAD MSRP",  always: true },
      { id: "cad_wholesale",    label: "CAD WS",    always: true },
      { id: "recurring_period", label: "Recurring", always: true },
      { id: "total_stock",      label: "Total",     always: true },
    ];
    const toggleable = TOGGLE_COLS.map((c) => ({ id: c.key, label: c.label }));
    const costCols = costAccess ? COST_COLS.map((c) => ({ id: c.key, label: c.label })) : [];
    const whCols = warehouses.map((w) => ({ id: `wh:${w.warehouse_id}`, label: w.name }));
    return [...ALWAYS, ...toggleable, ...costCols, ...whCols];
  })();
  const toolbarVisibleIds = mergedOrder.filter((k) => isVisible(k));
  const onToolbarToggle = (id) => {
    // Warehouse columns default ON (cols[key] !== false); toggle inverts.
    if (id.startsWith("wh:")) toggleColDefaultOn(id);
    else toggleCol(id);
  };
  const onToolbarMove = (id, dir) => {
    const cur = [...mergedOrder];
    const idx = cur.indexOf(id);
    const tgt = idx + dir;
    if (idx < 0 || tgt < 0 || tgt >= cur.length) return;
    [cur[idx], cur[tgt]] = [cur[tgt], cur[idx]];
    setColOrder(cur);
  };
  const onToolbarReset = () => { setColOrder([]); setCols({ ...DEFAULT_COLS }); };

  const onColDrop = (toKey) => {
    if (!dragKey || dragKey === toKey) { setDragKey(null); return; }
    const order = [...mergedOrder];
    const from = order.indexOf(dragKey), to = order.indexOf(toKey);
    if (from < 0 || to < 0) { setDragKey(null); return; }
    order.splice(to, 0, order.splice(from, 1)[0]);
    setColOrder(order); setDragKey(null);
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader testid="inventory-header" title="Inventory" subtitle="Items catalog and per-warehouse stock levels.">
        <div className="flex gap-2">
          <Button variant="outline" data-testid="count-sheet-button" onClick={() => openCountSheet()}>
            <Printer className="h-4 w-4" /> Count sheet
          </Button>
          {canEdit && <Button variant="outline" data-testid="reconcile-open-button" onClick={() => openReconcile()}>
            <ClipboardCheck className="h-4 w-4" /> Reconcile counts
          </Button>}
          <Button variant="outline" data-testid="reconcile-history-button" onClick={() => openHistory()}>
            <History className="h-4 w-4" /> History
          </Button>
          {canManage && <Button variant="outline" data-testid="manage-warehouses-button" onClick={() => setWhOpen(true)}><Warehouse className="h-4 w-4" /> Warehouses</Button>}
          {canCreate && <Button data-testid="add-item-button" onClick={openNew} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Add item</Button>}
        </div>
      </PageHeader>

      <div className="mb-4 grid grid-cols-3 gap-4">
        <Card className="border-slate-200 p-4" data-testid="inv-stat-items"><div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-slate-400">Items</span><Package className="h-4 w-4 text-primary" /></div><div className="mt-1 font-heading text-2xl font-bold text-slate-900">{items.length}</div></Card>
        <Card className="border-slate-200 p-4" data-testid="inv-stat-units"><div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-slate-400">Units in stock</span><Boxes className="h-4 w-4 text-primary" /></div><div className="mt-1 font-heading text-2xl font-bold text-slate-900">{totalUnits}</div></Card>
        <Card className="border-slate-200 p-4" data-testid="inv-stat-warehouses"><div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-slate-400">Warehouses</span><Warehouse className="h-4 w-4 text-primary" /></div><div className="mt-1 font-heading text-2xl font-bold text-slate-900">{warehouses.length}</div></Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-3">
          <Input data-testid="inventory-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search items by name, SKU, manufacturer or supplier…" className="max-w-md" />
          <div className="flex items-center gap-2">
            {/* Warehouse focus dropdown — filters items to those with stock in one warehouse
                and hides the other warehouse columns. Defaults to "HQ" if it exists. */}
            {warehouses.length > 0 && (
              <div className="inline-flex items-center gap-1.5" data-testid="inventory-warehouse-focus-wrap">
                <Warehouse className="h-3.5 w-3.5 text-slate-400" />
                <Select value={isFocused ? whFocus : "all"} onValueChange={(v) => setWhFocus(v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="inventory-warehouse-focus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="inventory-warehouse-focus-all">All warehouses</SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.warehouse_id} value={w.warehouse_id}
                        data-testid={`inventory-warehouse-focus-${w.warehouse_id}`}>
                        Only {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isFocused && (
                  <label className="inline-flex items-center gap-1 text-[11px] text-slate-500 cursor-pointer"
                    title="When on, items with 0 stock in the focused warehouse still appear in the list.">
                    <input type="checkbox" checked={whFocusIncludeZero}
                      onChange={(e) => setWhFocusIncludeZero(e.target.checked)}
                      data-testid="inventory-warehouse-focus-zero"
                      className="h-3 w-3" />
                    Include zeros
                  </label>
                )}
              </div>
            )}
            {(() => {
              // 3-state cycle: hide → show → only → hide
              const NEXT = { hide: "show", show: "only", only: "hide" };
              const META = {
                hide: { label: `Hidden: inactive (${inactiveCount})`, cls: "",                                         Icon: EyeOff,
                        title: "Currently hiding inactive items — click to show them alongside active ones." },
                show: { label: `Showing all · ${inactiveCount} inactive`, cls: "bg-slate-700 text-white hover:bg-slate-800", Icon: Eye,
                        title: "Currently showing everything — click to filter to inactive-only." },
                only: { label: `Inactive only (${inactiveCount})`,       cls: "bg-amber-500 text-white hover:bg-amber-600", Icon: Eye,
                        title: "Currently showing inactive items only — click to hide inactive again." },
              };
              const m = META[inactiveFilter];
              const Icon = m.Icon;
              return (
                <Button
                  variant={inactiveFilter === "hide" ? "outline" : "default"} size="sm"
                  data-testid="inventory-toggle-inactive"
                  data-inactive-mode={inactiveFilter}
                  onClick={() => setInactiveFilter(NEXT[inactiveFilter])}
                  className={m.cls}
                  title={m.title}
                >
                  <Icon className="h-4 w-4" />
                  {m.label}
                </Button>
              );
            })()}
            <Button variant="outline" size="sm" data-testid="export-csv-button" onClick={exportCsv} className="hidden"><Download className="h-4 w-4" /> Export CSV</Button>
            <ColumnsToolbar
              testidPrefix="inventory"
              count={filtered.length}
              columns={toolbarColumns}
              visibleIds={toolbarVisibleIds}
              onToggle={onToolbarToggle}
              onMove={onToolbarMove}
              onReset={onToolbarReset}
              onExportCsv={exportCsv}
              extraLeft={<span className="text-[11px] text-slate-400 mr-1" data-testid="inventory-count-secondary">of {items.length.toLocaleString()}</span>}
            />
          </div>
        </div>
        <div className="max-h-[65vh] overflow-auto">
          <table className="w-full table-fixed text-sm" style={{ minWidth: tableMinW }}>
            <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgb(226,232,240)]"><tr className="text-left text-xs uppercase text-slate-400">
              <th className="w-12 bg-white p-3">Img</th>
              {visibleCols.map((c) => (
                <th key={c.key} draggable onDragStart={() => setDragKey(c.key)} onDragEnd={() => setDragKey(null)}
                    onDragOver={(e) => e.preventDefault()} onDrop={() => onColDrop(c.key)} title="Drag to reorder"
                    className={`bg-white p-3 ${c.w} ${c.align === "right" ? "text-right" : ""} ${c.headClass || ""} cursor-move select-none ${dragKey === c.key ? "opacity-40" : ""}`}
                    data-testid={`colhead-${c.key}`}>
                  <span className={`inline-flex items-center gap-1 ${c.align === "right" ? "flex-row-reverse" : ""}`}
                        onClick={() => c.sort && toggleSort(c.sort)} data-testid={c.sort ? `sort-${c.sort}` : undefined}>
                    <GripVertical className="h-3 w-3 shrink-0 text-slate-300" />
                    {c.label}
                    {c.sort ? (sortKey === c.sort ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-30" />) : null}
                  </span>
                </th>
              ))}
              <th className="w-[72px] bg-white p-3"></th>
            </tr></thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 2} className="py-10 text-center text-slate-400">No items found.</td></tr>
              ) : ipg.pageItems.map((it) => (
                <tr key={it.item_id} className="border-b border-slate-50 align-top hover:bg-slate-50" data-testid={`item-row-${it.item_id}`}>
                  <td className="p-3">
                    {it.images && it.images.length > 0
                      ? <img src={it.images[0]} alt={it.name} className="h-9 w-9 rounded object-cover ring-1 ring-slate-200" />
                      : <div className="flex h-9 w-9 items-center justify-center rounded bg-slate-100 text-slate-300"><ImageIcon className="h-4 w-4" /></div>}
                  </td>
                  {visibleCols.map((c) => (
                    <td key={c.key} className={`p-3 align-top ${c.align === "right" ? "text-right" : ""} ${c.tdClass || ""}`}
                        data-testid={c.testid ? c.testid(it) : undefined}>
                      {c.render(it)}
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {canCreate && <button data-testid={`clone-item-${it.item_id}`} onClick={() => cloneItem(it)} title="Clone item" className="text-slate-400 hover:text-primary"><Copy className="h-4 w-4" /></button>}
                      {canEdit && warehouses.length >= 2 && <button data-testid={`transfer-item-${it.item_id}`} onClick={() => openTransfer(it)} title="Transfer stock" className="text-slate-400 hover:text-primary"><ArrowLeftRight className="h-4 w-4" /></button>}
                      {canEdit && <button data-testid={`edit-item-${it.item_id}`} onClick={() => openEdit(it)} title="Edit" className="text-slate-400 hover:text-primary"><Pencil className="h-4 w-4" /></button>}
                      {canManage && <button data-testid={`delete-item-${it.item_id}`} onClick={() => setDelTarget(it)} title="Delete" className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2">
          <PageSizeSelect pageSize={ipg.pageSize} setPageSize={ipg.setPageSize} testid="inventory-page-size" />
          <TablePagination {...ipg} testid="inventory-pagination" />
        </div>
      </Card>

      {/* Item dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit item" : "Add item"}</DialogTitle>
            <DialogDescription>Set pricing, currency and per-warehouse stock levels.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name</Label><Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>SKU</Label><Input data-testid="item-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Images <span className="text-xs font-normal text-slate-400">(up to 3, max 2MB each)</span></Label>
              <div className="flex flex-wrap items-center gap-2">
                {(form.images || []).map((src, i) => (
                  <div key={i} className="relative h-16 w-16 overflow-hidden rounded border border-slate-200" data-testid={`item-image-${i}`}>
                    <img src={src} alt={`img-${i}`} className="h-full w-full object-cover" />
                    <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute right-0 top-0 rounded-bl bg-black/55 p-0.5 text-white hover:bg-black/75"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {(form.images || []).length < 3 && (
                  <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded border border-dashed border-slate-300 text-slate-400 hover:border-primary hover:text-primary">
                    <Plus className="h-5 w-5" />
                    <span className="text-[10px]">Add</span>
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="item-image-upload" onChange={onImageFile} />
                  </label>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Manufacturer</Label><Input data-testid="item-manufacturer-input" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Supplier</Label><Input data-testid="item-supplier-input" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Supplier SKU</Label><Input data-testid="item-supplier-sku-input" value={form.supplier_sku} onChange={(e) => setForm({ ...form, supplier_sku: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Weight</Label><Input type="number" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} /></div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <Label className="text-xs uppercase tracking-wide text-slate-400">Pricing</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>CAD MSRP</Label><Input data-testid="item-cad-msrp-input" type="number" value={form.cad_msrp} onChange={(e) => setForm({ ...form, cad_msrp: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>CAD Wholesale</Label><Input data-testid="item-cad-ws-input" type="number" value={form.cad_wholesale} onChange={(e) => setForm({ ...form, cad_wholesale: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>USD MSRP</Label><Input data-testid="item-usd-msrp-input" type="number" value={form.usd_msrp} onChange={(e) => setForm({ ...form, usd_msrp: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>USD Wholesale</Label><Input data-testid="item-usd-ws-input" type="number" value={form.usd_wholesale} onChange={(e) => setForm({ ...form, usd_wholesale: e.target.value })} /></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Hardware / Service / One Time" /></div>
              <div className="space-y-1.5"><Label>Recurring</Label>
                <Select value={form.recurring_period} onValueChange={(v) => setForm({ ...form, recurring_period: v })}>
                  <SelectTrigger data-testid="item-recurring-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No">No</SelectItem><SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Quarterly">Quarterly</SelectItem><SelectItem value="Annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status || "active"} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="item-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="discontinued">Discontinued</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-400">Inactive items are hidden by default in the list, quote builder, and CSV exports.</p>
              </div>
            </div>
            {costAccess && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3" data-testid="vendor-cost-section">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wide text-amber-600">Vendor (UC) costs — staff only</Label>
                  <Button type="button" size="sm" variant="outline" data-testid="add-vendor-button" onClick={addVendor} className="h-7 border-amber-300 px-2 text-xs text-amber-700 hover:bg-amber-100"><Plus className="h-3.5 w-3.5" /> Add vendor</Button>
                </div>
                {(form.vendors || []).length === 0 ? (
                  <p className="mt-2 text-xs text-slate-400">No vendor costs yet. Add a primary vendor and any secondary/other vendors.</p>
                ) : (
                  <div className="mt-2 space-y-2">
                    {(form.vendors || []).map((v, i) => (
                      <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`vendor-row-${i}`}>
                        <Select value={v.vendor || undefined} onValueChange={(val) => updVendor(i, "vendor", val)}>
                          <SelectTrigger className="col-span-4 h-8" data-testid={`vendor-name-${i}`}><SelectValue placeholder="Select vendor" /></SelectTrigger>
                          <SelectContent>
                            {v.vendor && !warehouses.some((w) => w.name === v.vendor) && <SelectItem value={v.vendor}>{v.vendor}</SelectItem>}
                            {warehouses.map((w) => <SelectItem key={w.warehouse_id} value={w.name}>{w.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={v.rank} onValueChange={(val) => updVendor(i, "rank", val)}>
                          <SelectTrigger className="col-span-3 h-8" data-testid={`vendor-rank-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{VENDOR_RANKS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="col-span-2 h-8" type="number" placeholder="CAD" data-testid={`vendor-cad-${i}`} value={v.cost_cad} onChange={(e) => updVendor(i, "cost_cad", e.target.value)} />
                        <Input className="col-span-2 h-8" type="number" placeholder="USD" data-testid={`vendor-usd-${i}`} value={v.cost_usd} onChange={(e) => updVendor(i, "cost_usd", e.target.value)} />
                        <button type="button" className="col-span-1 text-slate-300 hover:text-red-500" data-testid={`vendor-remove-${i}`} onClick={() => rmVendor(i)}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Stock per warehouse</Label>
              {warehouses.length === 0 ? <p className="text-xs text-slate-400">Add a warehouse first to track stock.</p> : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {warehouses.map((w) => (
                    <div key={w.warehouse_id} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-slate-600">{w.name} <span className="text-xs text-slate-400">{w.location}</span></span>
                      <Input className="h-8 w-24" type="number" data-testid={`item-stock-${w.warehouse_id}`}
                        value={form.stock[w.warehouse_id] ?? 0}
                        onChange={(e) => setForm({ ...form, stock: { ...form.stock, [w.warehouse_id]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-item-button" onClick={save} className="bg-primary hover:bg-primary/90 text-primary-foreground">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warehouses dialog */}
      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Warehouses</DialogTitle>
            <DialogDescription>Add or remove stock locations used across your inventory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Input data-testid="wh-name-input" placeholder="Warehouse name" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} />
              <Input data-testid="wh-location-input" placeholder="Location" value={whForm.location} onChange={(e) => setWhForm({ ...whForm, location: e.target.value })} />
              <Button data-testid="add-warehouse-button" onClick={addWarehouse} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> Add warehouse</Button>
            </div>
            <div className="divide-y divide-slate-100">
              {warehouses.map((w) => (
                <div key={w.warehouse_id} className="flex items-center justify-between gap-2 py-2" data-testid={`wh-row-${w.warehouse_id}`}>
                  {whEdit?.warehouse_id === w.warehouse_id ? (
                    <>
                      <div className="flex-1 space-y-1.5">
                        <Input className="h-8" data-testid={`wh-edit-name-${w.warehouse_id}`} placeholder="Warehouse name" value={whEdit.name} onChange={(e) => setWhEdit({ ...whEdit, name: e.target.value })} />
                        <Input className="h-8" data-testid={`wh-edit-location-${w.warehouse_id}`} placeholder="Location" value={whEdit.location} onChange={(e) => setWhEdit({ ...whEdit, location: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={saveWhEdit} data-testid={`wh-save-${w.warehouse_id}`} className="text-slate-400 hover:text-green-600"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setWhEdit(null)} data-testid={`wh-cancel-${w.warehouse_id}`} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div><div className="text-sm font-medium text-slate-800">{w.name}</div><div className="text-xs text-slate-400">{w.location || "—"}</div></div>
                      <div className="flex items-center gap-2.5">
                        <button onClick={() => startWhEdit(w)} data-testid={`wh-edit-${w.warehouse_id}`} className="text-slate-300 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => delWarehouse(w.warehouse_id)} data-testid={`wh-del-${w.warehouse_id}`} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!xfer} onOpenChange={(o) => !o && setXfer(null)}>
        <DialogContent className="max-w-md" data-testid="transfer-stock-dialog">
          <DialogHeader>
            <DialogTitle>Transfer stock</DialogTitle>
            <DialogDescription>Move units of <span className="font-medium text-slate-700">{xfer?.item?.name}</span> between warehouses.</DialogDescription>
          </DialogHeader>
          {xfer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>From warehouse</Label>
                  <Select value={xfer.from} onValueChange={(v) => setXfer({ ...xfer, from: v })}>
                    <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Source" /></SelectTrigger>
                    <SelectContent>
                      {warehouses.filter((w) => ((xfer.item.stock && xfer.item.stock[w.warehouse_id]) || 0) > 0).map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}{w.location ? ` (${w.location})` : ""} · {(xfer.item.stock && xfer.item.stock[w.warehouse_id]) || 0}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>To warehouse</Label>
                  <Select value={xfer.to} onValueChange={(v) => setXfer({ ...xfer, to: v })}>
                    <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Destination" /></SelectTrigger>
                    <SelectContent>
                      {warehouses.filter((w) => w.warehouse_id !== xfer.from).map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.name}{w.location ? ` (${w.location})` : ""} · {(xfer.item.stock && xfer.item.stock[w.warehouse_id]) || 0}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Quantity{xfer.from ? ` (max ${(xfer.item.stock && xfer.item.stock[xfer.from]) || 0})` : ""}</Label>
                <Input type="number" min="1" data-testid="transfer-qty-input" value={xfer.qty} onChange={(e) => setXfer({ ...xfer, qty: e.target.value })} placeholder="0" />
              </div>
              <div className="space-y-1.5" data-testid="transfer-movements">
                <Label className="text-xs text-slate-500">Recent movements</Label>
                {xferMoves.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400" data-testid="transfer-movements-empty">No prior transfers for this item yet. Movements will appear here after your first transfer.</p>
                ) : (
                  <div className="max-h-40 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                    {xferMoves.map((m) => (
                      <div key={m.movement_id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                        <div className="flex items-center gap-1.5 text-slate-600">
                          <span className="font-medium text-slate-800">{m.quantity}</span>
                          <span className="text-slate-400">{m.from_warehouse_name}</span>
                          <ArrowLeftRight className="h-3 w-3 text-slate-300" />
                          <span className="text-slate-400">{m.to_warehouse_name}</span>
                        </div>
                        <span className="whitespace-nowrap text-[11px] text-slate-400">{m.user_name} · {new Date(m.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setXfer(null)}>Cancel</Button>
            <Button data-testid="transfer-submit-button" onClick={submitTransfer} className="bg-primary hover:bg-primary/90 text-primary-foreground"><ArrowLeftRight className="h-4 w-4" /> Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete item?</AlertDialogTitle><AlertDialogDescription>Removes {delTarget?.name} from the catalog.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-item" onClick={del} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* --------- Count-sheet dialog --------- */}
      <Dialog open={csOpen} onOpenChange={setCsOpen}>
        <DialogContent className="max-w-lg" data-testid="count-sheet-dialog">
          <DialogHeader>
            <DialogTitle>Print inventory count sheet</DialogTitle>
            <DialogDescription>
              Generates a printable PDF for physical counting — one warehouse, one page range. Only <b>active</b> items are included.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Warehouse</Label>
              <Select value={csWid} onValueChange={setCsWid}>
                <SelectTrigger data-testid="count-sheet-warehouse"><SelectValue placeholder="Choose a warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.warehouse_id} value={w.warehouse_id}
                      data-testid={`count-sheet-warehouse-${w.warehouse_id}`}>
                      {w.name}{w.location ? ` — ${w.location}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Filter by search</Label>
                <Input value={csSearch} onChange={(e) => setCsSearch(e.target.value)}
                  placeholder="e.g. Htek, Yealink, UC-921G" data-testid="count-sheet-search" />
                <div className="text-[10px] text-slate-400">Matches name, SKU, manufacturer, or supplier.</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Count date</Label>
                <Input type="date" value={csDate} onChange={(e) => setCsDate(e.target.value)}
                  data-testid="count-sheet-date" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="count-sheet-include-zero-label">
              <input type="checkbox" checked={csIncludeZero} onChange={(e) => setCsIncludeZero(e.target.checked)}
                data-testid="count-sheet-include-zero" className="h-3.5 w-3.5" />
              Include items with 0 expected stock in this warehouse
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCsOpen(false)} data-testid="count-sheet-cancel">Cancel</Button>
            <Button onClick={downloadCountSheet} disabled={csLoading || !csWid}
              data-testid="count-sheet-download"
              className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {csLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Reconcile-counts dialog --------- */}
      <Dialog open={rcOpen} onOpenChange={setRcOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col p-0" data-testid="reconcile-dialog">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Reconcile physical count</DialogTitle>
            <DialogDescription>
              Type the actual quantity found for each item at the picked warehouse (or paste them from a CSV). Rows you leave blank are ignored. Preview before applying — the &ldquo;Apply&rdquo; step logs one Stock Movement per changed item.
            </DialogDescription>
          </DialogHeader>

          {/* Top toolbar */}
          <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-6 py-3">
            <div className="min-w-[220px] flex-1">
              <Label className="text-xs font-medium text-slate-600">Warehouse</Label>
              <Select value={rcWid} onValueChange={(v) => { setRcWid(v); setRcPreview(null); }}>
                <SelectTrigger data-testid="reconcile-warehouse"><SelectValue placeholder="Choose a warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.warehouse_id} value={w.warehouse_id}
                      data-testid={`reconcile-warehouse-${w.warehouse_id}`}>
                      {w.name}{w.location ? ` — ${w.location}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[220px] flex-1">
              <Label className="text-xs font-medium text-slate-600">Filter</Label>
              <Input value={rcSearch} onChange={(e) => setRcSearch(e.target.value)}
                placeholder="e.g. Htek, Yealink, UC-921G" data-testid="reconcile-search" />
            </div>
            <div className="ml-auto flex gap-2">
              <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                data-testid="reconcile-upload-csv-label">
                <Upload className="h-3.5 w-3.5" /> Upload CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={rcUploadCsv}
                  data-testid="reconcile-upload-csv" />
              </label>
              <Button variant="ghost" size="sm" onClick={() => { setRcCounts({}); setRcPreview(null); }}
                data-testid="reconcile-clear" title="Clear all typed counts">
                <X className="h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto px-6 py-2">
            {!rcWid ? (
              <p className="py-8 text-center text-xs text-slate-400">Pick a warehouse to start.</p>
            ) : reconcileRows.length === 0 ? (
              <p className="py-8 text-center text-xs text-slate-400">No active items match this filter.</p>
            ) : (
              <table className="w-full text-sm" data-testid="reconcile-grid">
                <thead className="sticky top-0 z-10 bg-white text-[11px] uppercase tracking-wide text-slate-500">
                  <tr className="border-b border-slate-200">
                    <th className="px-2 py-2 text-left">SKU</th>
                    <th className="px-2 py-2 text-left">Item</th>
                    <th className="px-2 py-2 text-right w-20">Expected</th>
                    <th className="px-2 py-2 text-right w-24">Counted</th>
                    <th className="px-2 py-2 text-right w-20">Δ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reconcileRows.map((row) => {
                    const cur = rcCounts[row.item_id];
                    const parsed = cur === "" || cur == null ? null : Math.max(0, parseInt(cur, 10) || 0);
                    const delta = parsed == null ? null : parsed - row.expected;
                    return (
                      <tr key={row.item_id} data-testid={`reconcile-row-${row.item_id}`}>
                        <td className="px-2 py-1.5 font-mono text-xs text-slate-600">{row.sku || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-800">{row.name}</td>
                        <td className="px-2 py-1.5 text-right text-slate-500">{row.expected}</td>
                        <td className="px-2 py-1.5">
                          <Input type="number" min="0" step="1" value={cur ?? ""}
                            onChange={(e) => { setRcCounts((p) => ({ ...p, [row.item_id]: e.target.value })); setRcPreview(null); }}
                            className="h-7 text-right text-sm"
                            data-testid={`reconcile-input-${row.item_id}`} />
                        </td>
                        <td className={`px-2 py-1.5 text-right font-semibold ${
                          delta == null ? "text-slate-300" :
                          delta > 0     ? "text-emerald-600" :
                          delta < 0     ? "text-red-600"     : "text-slate-400"
                        }`} data-testid={`reconcile-delta-${row.item_id}`}>
                          {delta == null ? "—" : delta > 0 ? `+${delta}` : delta}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Preview banner + notes + footer */}
          {rcPreview && (
            <div className="border-t border-slate-100 bg-amber-50/60 px-6 py-3 text-xs"
              data-testid="reconcile-preview-banner">
              <div className="flex items-center gap-2 font-semibold text-amber-800">
                <AlertCircle className="h-4 w-4" />
                Preview — {rcPreview.adjusted} item(s) will change, {rcPreview.unchanged} unchanged
                {rcPreview.not_found?.length ? ` · ${rcPreview.not_found.length} not found` : ""}
              </div>
              <div className="mt-1 text-amber-700">
                Click <span className="font-semibold">Apply reconciliation</span> to write a Stock Movement per changed row and update the catalog.
              </div>
            </div>
          )}
          <div className="border-t border-slate-100 px-6 py-3">
            <Label className="text-xs font-medium text-slate-600">Notes (optional — attached to every movement)</Label>
            <Textarea rows={2} value={rcNotes} onChange={(e) => setRcNotes(e.target.value)}
              placeholder="e.g. Q3 physical count · counted by Alex" data-testid="reconcile-notes" />
          </div>
          <DialogFooter className="border-t border-slate-100 px-6 py-3">
            <div className="mr-auto text-xs text-slate-500" data-testid="reconcile-touched-count">
              {rcTouchedIds.length} item(s) with a typed count
            </div>
            <Button variant="outline" onClick={() => setRcOpen(false)} data-testid="reconcile-cancel">Cancel</Button>
            <Button variant="outline" onClick={runReconcilePreview} disabled={rcSubmitting || !rcTouchedIds.length}
              data-testid="reconcile-preview">
              {rcSubmitting && !rcPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
              Preview
            </Button>
            <Button onClick={runReconcileApply} disabled={rcSubmitting || !rcPreview || !rcPreview.adjusted}
              data-testid="reconcile-apply"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {rcSubmitting && rcPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Apply reconciliation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --------- Reconciliation history dialog --------- */}
      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0" data-testid="history-dialog">
          <DialogHeader className="border-b border-slate-100 px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              {histDetail ? (
                <button type="button" onClick={() => setHistDetail(null)}
                  data-testid="history-back"
                  className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
                  <ArrowLeft className="h-4 w-4" /> Back to history
                </button>
              ) : (
                <DialogTitle>Reconciliation history</DialogTitle>
              )}
              {!histDetail && (
                <Select value={histWid} onValueChange={(v) => { setHistWid(v); loadHistory(v); }}>
                  <SelectTrigger className="h-8 w-[220px] text-xs" data-testid="history-warehouse-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All warehouses</SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.warehouse_id} value={w.warehouse_id}
                        data-testid={`history-warehouse-${w.warehouse_id}`}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {histDetail && (
              <DialogDescription className="mt-2 text-xs">
                <span className="font-semibold text-slate-700">{histDetail.warehouse_name}</span>
                {" · "}by <span className="font-medium">{histDetail.counted_by_name || "—"}</span>
                {" · "}{histDetail.created_at ? new Date(histDetail.created_at).toLocaleString() : "—"}
                {histDetail.notes && <> · notes: <span className="italic">{histDetail.notes}</span></>}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-3">
            {histLoading ? (
              <p className="py-8 text-center text-xs text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</p>
            ) : histDetail ? (
              histDetailLoading ? (
                <p className="py-8 text-center text-xs text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading movements…</p>
              ) : (
                <table className="w-full text-sm" data-testid="history-detail-grid">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-2 py-2 text-left">SKU</th>
                      <th className="px-2 py-2 text-left">Item</th>
                      <th className="px-2 py-2 text-right w-20">Before</th>
                      <th className="px-2 py-2 text-right w-20">After</th>
                      <th className="px-2 py-2 text-right w-20">Δ</th>
                      <th className="px-2 py-2 text-left w-24">Direction</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {histDetail.movements.map((m) => {
                      const delta = (m.to_after ?? 0) - (m.to_before ?? 0);
                      return (
                        <tr key={m.movement_id} data-testid={`history-detail-row-${m.movement_id}`}>
                          <td className="px-2 py-1.5 font-mono text-xs text-slate-600">{m.sku || "—"}</td>
                          <td className="px-2 py-1.5 text-slate-800">{m.item_name}</td>
                          <td className="px-2 py-1.5 text-right text-slate-500">{m.to_before ?? 0}</td>
                          <td className="px-2 py-1.5 text-right text-slate-700 font-semibold">{m.to_after ?? 0}</td>
                          <td className={`px-2 py-1.5 text-right font-semibold ${
                            delta > 0 ? "text-emerald-600" : delta < 0 ? "text-red-600" : "text-slate-400"
                          }`}>{delta > 0 ? `+${delta}` : delta}</td>
                          <td className="px-2 py-1.5 text-xs">
                            <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium ${
                              m.direction === "gain"      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : m.direction === "shrinkage" ? "border-red-200 bg-red-50 text-red-700"
                            :                                "border-slate-200 bg-slate-50 text-slate-600"
                            }`}>{m.direction || "—"}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            ) : histRows.length === 0 ? (
              <p className="py-8 text-center text-xs text-slate-400" data-testid="history-empty">
                No reconciliations yet{histWid !== "all" ? " for this warehouse" : ""}.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="history-list-grid">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-2 py-2 text-left">Date</th>
                    {histWid === "all" && <th className="px-2 py-2 text-left">Warehouse</th>}
                    <th className="px-2 py-2 text-left">Counted by</th>
                    <th className="px-2 py-2 text-right w-20">Items</th>
                    <th className="px-2 py-2 text-right w-20">Δ Total</th>
                    <th className="px-2 py-2 text-left">Notes</th>
                    <th className="px-2 py-2 text-right w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {histRows.map((r) => (
                    <tr key={r.reconciliation_id} className="cursor-pointer hover:bg-primary/5"
                      onClick={() => openHistoryDetail(r.reconciliation_id)}
                      data-testid={`history-row-${r.reconciliation_id}`}>
                      <td className="px-2 py-1.5 text-slate-700">{new Date(r.created_at).toLocaleString()}</td>
                      {histWid === "all" && <td className="px-2 py-1.5 font-medium text-slate-800">{r.warehouse_name || "—"}</td>}
                      <td className="px-2 py-1.5 text-slate-700">{r.counted_by_name || "—"}</td>
                      <td className="px-2 py-1.5 text-right text-slate-700">{r.item_count}</td>
                      <td className={`px-2 py-1.5 text-right font-semibold ${
                        r.total_delta > 0 ? "text-emerald-600" : r.total_delta < 0 ? "text-red-600" : "text-slate-400"
                      }`}>{r.total_delta > 0 ? `+${r.total_delta}` : r.total_delta}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-500 truncate max-w-[240px]" title={r.notes}>{r.notes || "—"}</td>
                      <td className="px-2 py-1.5 text-right text-slate-300">›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="border-t border-slate-100 px-6 py-3">
            <div className="mr-auto text-xs text-slate-500">
              {histDetail
                ? `${histDetail.movements.length} movement(s)`
                : `${histRows.length} reconciliation(s)`}
            </div>
            <Button variant="outline" onClick={() => setHistOpen(false)} data-testid="history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
